/**
 * フォーム自動バックアップ ユーティリティ関数
 *
 * フォーム入力途中のデータをlocalStorageにバックアップし、
 * リロードや誤操作によるデータ消失を防ぐ機能を提供します。
 */
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

export const BACKUP_KEY_PREFIX = 'form_backup_';
export const DEFAULT_EXPIRATION_DAYS = 7;
export const DEFAULT_DEBOUNCE_MS = 500;

/**
 * バックアップデータの型定義
 */
export interface FormBackupData<T = Record<string, unknown>> {
  formType: string;
  entityId: string;
  data: T;
  savedAt: number;
  version: number;
}

type SerializedValue =
  | { __formBackupType: 'dayjs'; value: string }
  | { __formBackupType: 'date'; value: string }
  | unknown;

/**
 * バックアップキーを生成する
 * @param formType フォームの種類（例: 'equipment', 'part'）
 * @param entityId エンティティID（新規作成の場合は 'new'）
 * @returns バックアップキー
 */
export function getBackupKey(
  formType: string,
  entityId: string | number | null | undefined
): string {
  const id = entityId != null ? String(entityId) : 'new';
  return `${BACKUP_KEY_PREFIX}${formType}_${id}`;
}

/**
 * バックアップが有効期限切れかどうかを判定する
 * @param savedAt 保存日時（Unix timestamp）
 * @param expirationDays 有効期限（日数）
 * @returns 有効期限切れの場合 true
 */
export function isBackupExpired(
  savedAt: number,
  expirationDays: number = DEFAULT_EXPIRATION_DAYS
): boolean {
  const expirationMs = expirationDays * 24 * 60 * 60 * 1000;
  return Date.now() - savedAt > expirationMs;
}

/**
 * バックアップをlocalStorageから取得する
 * @param key バックアップキー
 * @returns バックアップデータ（存在しない場合は null）
 */
export function getBackupFromStorage<T = Record<string, unknown>>(
  key: string
): FormBackupData<T> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const data = JSON.parse(raw) as FormBackupData<T>;

    // 必須フィールドの検証
    if (
      typeof data.formType !== 'string' ||
      typeof data.entityId !== 'string' ||
      typeof data.savedAt !== 'number' ||
      typeof data.version !== 'number' ||
      data.data === undefined
    ) {
      // 無効なデータは削除
      localStorage.removeItem(key);
      return null;
    }

    const restoredData = deserializeValue(data.data) as T;

    return { ...data, data: restoredData };
  } catch {
    // JSON パースエラー等の場合は削除
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * バックアップをlocalStorageに保存する
 * @param key バックアップキー
 * @param formType フォームの種類
 * @param entityId エンティティID
 * @param data フォームデータ
 */
export function saveBackupToStorage<T = Record<string, unknown>>(
  key: string,
  formType: string,
  entityId: string,
  data: T
): void {
  const serializedData = serializeValue(data) as T;

  const backupData: FormBackupData<T> = {
    formType,
    entityId,
    data: serializedData,
    savedAt: Date.now(),
    version: 1,
  };

  try {
    localStorage.setItem(key, JSON.stringify(backupData));
  } catch (error) {
    // localStorage が満杯の場合等のエラーハンドリング
    console.warn('Failed to save form backup:', error);
    // 古いバックアップをクリーンアップして再試行
    cleanupExpiredBackups();
    try {
      localStorage.setItem(key, JSON.stringify(backupData));
    } catch {
      console.error('Failed to save form backup after cleanup');
    }
  }
}

/**
 * バックアップをlocalStorageから削除する
 * @param key バックアップキー
 */
export function removeBackupFromStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to remove form backup:', error);
  }
}

/**
 * localStorage のすべてのキーを取得する
 * テスト環境での互換性のため、for ループを使用
 */
function getLocalStorageKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * 有効期限切れのバックアップをすべて削除する
 */
export function cleanupExpiredBackups(
  expirationDays: number = DEFAULT_EXPIRATION_DAYS
): void {
  try {
    const keys = getLocalStorageKeys().filter((key) =>
      key.startsWith(BACKUP_KEY_PREFIX)
    );

    keys.forEach((key) => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return;

        const data = JSON.parse(raw);
        if (
          typeof data.savedAt === 'number' &&
          isBackupExpired(data.savedAt, expirationDays)
        ) {
          localStorage.removeItem(key);
        }
      } catch {
        // 無効なデータは削除
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Failed to cleanup expired backups:', error);
  }
}

/**
 * バックアップの保存日時をフォーマットする
 * @param timestamp 保存日時（Unix timestamp）
 * @returns フォーマットされた日時文字列
 */
export function formatBackupTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Dayjs/Date を安全にシリアライズする
 */
function serializeValue(value: unknown): SerializedValue {
  if (value === null || value === undefined) return value as SerializedValue;

  if (dayjs.isDayjs(value)) {
    const dj = value as Dayjs;
    return { __formBackupType: 'dayjs', value: dj.toISOString() };
  }

  if (value instanceof Date) {
    return { __formBackupType: 'date', value: value.toISOString() };
  }

  if (Array.isArray(value)) {
    return value.map(serializeValue) as unknown as SerializedValue;
  }

  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      result[k] = serializeValue(v);
    });
    return result as SerializedValue;
  }

  return value as SerializedValue;
}

/**
 * シリアライズ済みデータを元の形に戻す
 */
function deserializeValue(value: unknown): unknown {
  if (value === null || value === undefined) return value;

  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value as { __formBackupType?: string }).__formBackupType
  ) {
    const marker = (value as { __formBackupType: string; value: string }).__formBackupType;
    const stored = (value as { value: string }).value;
    if (marker === 'dayjs') return dayjs(stored);
    if (marker === 'date') return dayjs(stored);
  }

  // 既存バックアップの dayjs シリアライズ痕跡 ($d フィールド等) を復元
  if (isLegacyDayjsObject(value)) {
    return dayjs((value as { $d: string | number | Date }).$d);
  }

  if (typeof value === 'string' && looksLikeIsoDateString(value)) {
    return dayjs(value);
  }

  if (Array.isArray(value)) {
    return value.map(deserializeValue);
  }

  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      result[k] = deserializeValue(v);
    });
    return result;
  }

  return value;
}

function isLegacyDayjsObject(value: unknown): value is { $d: string | number | Date } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return (
    '$d' in value &&
    typeof (value as { $d: unknown }).$d !== 'undefined' &&
    ('$y' in value || '$M' in value || '$D' in value)
  );
}

function looksLikeIsoDateString(value: string): boolean {
  // e.g. 2025-12-01T09:15:30.000Z or without milliseconds/timezone
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:?\d{2})?$/.test(value);
}

/**
 * すべてのフォームバックアップを取得する（デバッグ用）
 * @returns バックアップデータの配列
 */
export function getAllBackups(): Array<{
  key: string;
  data: FormBackupData | null;
}> {
  const keys = getLocalStorageKeys().filter((key) =>
    key.startsWith(BACKUP_KEY_PREFIX)
  );

  return keys.map((key) => ({
    key,
    data: getBackupFromStorage(key),
  }));
}

/**
 * すべてのフォームバックアップを削除する（デバッグ用）
 */
export function clearAllBackups(): void {
  const keys = getLocalStorageKeys().filter((key) =>
    key.startsWith(BACKUP_KEY_PREFIX)
  );

  keys.forEach((key) => {
    localStorage.removeItem(key);
  });
}
